import { AidaService, ILlmDashboardPayload, LlmArtifactService, LlmConversationNode } from '@adaptiva/ai/common';
import { IChatMessage } from '@adaptiva/core/chat';
import { ErrorHandlerService } from '@adaptiva/core/error-handler';
import { IUserDashboard, ObjectManagerService } from '@adaptiva/data-service';
import { BaseBubbleComponent, ChatBubbleComponent } from '@adaptiva/widgets/chat';
import { SpinnerComponent } from '@adaptiva/widgets/spinner';
import { ChangeDetectionStrategy, Component, effect, EffectRef, inject, signal, WritableSignal } from '@angular/core';
import { Observable, of, Subscription, switchMap, tap } from 'rxjs';

@Component({
	imports: [
		ChatBubbleComponent,
		SpinnerComponent,
	],
	selector: 'div[ada-dashboard-bubble]',
	styleUrl: './dashboard-bubble.component.scss',
	templateUrl: './dashboard-bubble.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardBubbleComponent extends BaseBubbleComponent<LlmConversationNode<ILlmDashboardPayload>> {

	protected readonly artifactService: LlmArtifactService = inject(LlmArtifactService);

	protected readonly objectManager: ObjectManagerService = inject(ObjectManagerService);

	protected readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

	protected readonly dashboard: WritableSignal<IUserDashboard> = signal(null);

	protected readonly aida: AidaService = inject(AidaService);

	protected readonly dashboardFx: EffectRef = effect(() => {
		const _message: IChatMessage<LlmConversationNode<ILlmDashboardPayload>> = this.message();
		const _target: ILlmDashboardPayload | null = _message.data.payload;

		this.dashboardSubscription?.unsubscribe();
		let _payloadData: Observable<ILlmDashboardPayload>;

		if (_target)
			_payloadData = of(_target);
		else {
			_payloadData = this.aida.regenerateNode(_message.data.nodeId).pipe(
				tap((_payload: ILlmDashboardPayload) => {
					_message.data.payload = _payload;
				})
			);
		}

		this.dashboardSubscription = _payloadData.pipe(
			this.takeOneUntilDestroyed(),
			switchMap((_payload: ILlmDashboardPayload) => {
				return this.objectManager.streamObject<IUserDashboard>({
					fetch: () => this.objectManager.getObjectById(
						_payload.javaClassName,
						_payload.objectId,
					),
				});
			})
		).subscribe({
			next: d => this.dashboard.set(d),
			error: (e: Error) => {
				this.errorHandler.defaultErrorHandler(e, 'DashboardBubbleComponent.dashboardFx');
			}
		});
	});

	protected dashboardSubscription!: Subscription;

	protected openDashboard(): void {
		try {
			this.artifactService.open(this.message().data);
		} catch (e) {
			this.errorHandler.defaultErrorHandler(e, 'DashboardBubbleComponent.openDashboard');
		}
	}

}
